import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import DepartementNode from './DepartementNode';

const OrganigrammeTreeCard = ({
  loading,
  draggedItem,
  companyPrimaryColor,
  companyLogoUrl,
  companyLegalName,
  handleDragOver,
  handleDropToRoot,
  departements,
  nodeProps,
}) => {
  const { t } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center space-x-2">
        <Building className="h-4 w-4" />
        <span>{t('organigramme.tree.structureTitle')}</span>
      </CardTitle>
      <CardDescription>
        {t('organigramme.dragHint')}
      </CardDescription>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="space-y-2 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            <p className="text-sm text-muted-foreground">{t('organigramme.loading')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
              draggedItem
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/50 hover:bg-muted/5'
            }`}
            style={{
              borderColor: draggedItem ? companyPrimaryColor : undefined,
              backgroundColor: draggedItem ? `${companyPrimaryColor}15` : undefined,
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDropToRoot(e)}
          >
            <div className="flex flex-col items-center mb-4">
              <div className="mb-3 p-3 rounded-full" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
                {companyLogoUrl ? (
                  <img
                    src={companyLogoUrl}
                    alt={`Logo ${companyLegalName}`}
                    className="h-14 w-14 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <Building
                  className="h-10 w-10"
                  style={{
                    color: companyPrimaryColor,
                    display: companyLogoUrl ? 'none' : 'block',
                  }}
                />
              </div>

              <div className="space-y-1">
                <span
                  className="font-bold text-xl block"
                  style={{ color: draggedItem ? companyPrimaryColor : 'var(--lm-ink)' }}
                >
                  {companyLegalName}
                </span>
                <div
                  className="h-0.5 w-16 mx-auto rounded-full"
                  style={{ backgroundColor: companyPrimaryColor + '60' }}
                />
              </div>
            </div>

            <div className="space-y-3">
              {draggedItem && (
                <p className="text-sm font-medium" style={{ color: companyPrimaryColor }}>
                  {`Déposer "${draggedItem.name}" ici pour en faire un département racine`}
                </p>
              )}
              {draggedItem && (
                <div
                  className="mt-4 pt-3 border-t border-dashed"
                  style={{ borderColor: companyPrimaryColor + '40' }}
                >
                  <div
                    className="flex items-center justify-center space-x-3 text-sm font-medium"
                    style={{ color: companyPrimaryColor }}
                  >
                    <Building className="h-5 w-5" />
                    <span>→</span>
                    <span className="px-2 py-1 rounded-md" style={{ backgroundColor: "var(--lm-s2)" }}>{draggedItem.name}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {departements.map((departement) => (
              <DepartementNode key={departement.id} departement={departement} {...nodeProps} />
            ))}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default OrganigrammeTreeCard;
